import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();

  // Admin widgets
  const adminWidgets = [
    {
      title: t('admin.seasonManagement.title'),
      description: t('admin.createNewSeasons'),
      icon: '🏆',
      link: '/admin/season',
      color: 'bg-blue-50 dark:bg-blue-900/200'
    },
    {
      title: t('admin.gamesManagement'),
      description: t('admin.scheduleGames'),
      icon: '🏀',
      link: '/admin/games',
      color: 'bg-green-500'
    },
    {
      title: t('admin.playerManagement'),
      description: t('admin.managePlayerRosters'),
      icon: '👥',
      link: '/admin/players',
      color: 'bg-purple-500'
    },
    {
      title: t('admin.tradeManagement'),
      description: t('admin.reviewTrades'),
      icon: '🔄',
      link: '/admin/trades',
      color: 'bg-orange-500'
    },
    {
      title: t('admin.teamManagement'),
      description: t('admin.manageTeamsAssign'),
      icon: '🏟️',
      link: '/admin/teams',
      color: 'bg-red-500'
    },
    {
      title: t('admin.userManagement'),
      description: t('admin.manageUserAccounts'),
      icon: '👤',
      link: '/admin/users',
      color: 'bg-indigo-500'
    },
    {
      title: 'Team Applications',
      description: t('admin.reviewApplications'),
      icon: '📝',
      link: '/admin/applications',
      color: 'bg-pink-500'
    },
    {
      title: 'Manage Rookies',
      description: 'Set rookie status for award tracking',
      icon: '🌟',
      link: '/admin/rookies',
      color: 'bg-yellow-500'
    }
  ];

  // Team owner widgets
  const ownerWidgets = [
    {
      title: t('admin.myGames'),
      description: t('admin.viewScheduleResults'),
      icon: '🏀',
      link: '/admin/games',
      color: 'bg-green-500'
    },
    {
      title: t('admin.myTrades'),
      description: t('admin.proposeTradesHistory'),
      icon: '🔄',
      link: '/admin/trades',
      color: 'bg-orange-500'
    },
    {
      title: t('admin.teamSettings'),
      description: t('admin.updateTeamInfo'),
      icon: '⚙️',
      link: '/settings',
      color: 'bg-gray-50 dark:bg-gray-8000'
    }
  ];

  const widgets = isAdmin() ? adminWidgets : ownerWidgets;

  return (
    <div>
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">
          {t('admin.welcomeBack', { username: user.username })}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {isAdmin()
            ? t('admin.adminWelcomeText')
            : t('admin.ownerWelcomeText', { teamName: user.team?.name || 'your team' })}
        </p>
      </div>

      {/* Stats Cards (if admin) */}
      {isAdmin() && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">{t('admin.totalTeams')}</div>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">30</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">{t('admin.totalPlayers')}</div>
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">390</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">{t('admin.activeSeason')}</div>
            <div className="text-3xl font-bold text-purple-600">2025</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">{t('admin.totalUsers')}</div>
            <div className="text-3xl font-bold text-orange-600">-</div>
          </div>
        </div>
      )}

      {/* Quick Actions Grid */}
      <div className="mb-8">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">{t('admin.quickActions')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {widgets.map((widget) => (
            <Link
              key={widget.link}
              to={widget.link}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow hover:shadow-lg dark:shadow-gray-900 transition-shadow"
            >
              <div className="flex items-start">
                <div className={`${widget.color} p-3 rounded-lg text-white text-2xl mr-4`}>
                  {widget.icon}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    {widget.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{widget.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity (Placeholder) */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">{t('admin.recentActivity')}</h3>
        <div className="text-gray-500 dark:text-gray-400 text-center py-8">
          {t('admin.noRecentActivity')}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
